import type { Policy } from '@mls-wallet/shared-types';
import { toHumanReadableTokenAmount } from './tokenAmountUtils';

function getSingleDestinationLabel(policy: Policy): string | null {
  if (policy.transactionType !== 'TOKEN_TRANSFER') {
    return null;
  }

  const condition = policy.tokenTransferCondition;
  const destinationAddresses = condition?.destinationAddresses ?? [];
  if (destinationAddresses.length !== 1) {
    return null;
  }

  const address = destinationAddresses[0];
  const matchingEntry = condition?.destinationAddressEntries?.find(
    (entry) => entry.address.toLowerCase() === address?.toLowerCase()
  );

  return matchingEntry?.name ?? address ?? null;
}

/**
 * Formats policy details text for display in the bottom-left corner
 * Returns an array of text segments with some marked as pills (highlighted values)
 *
 * TODO: This is created before policy limitations are implemented, refine after
 * DEN-224 is merged.
 */
export function formatPolicyDetailsText(
  policy: Policy
): Array<{ text: string; isPill: boolean }> {
  const segments: Array<{ text: string; isPill: boolean }> = [];

  if (policy.transactionType === 'TOKEN_TRANSFER') {
    const condition = policy.tokenTransferCondition;

    // Display token amount with symbol, use "Transfer up to" when there's an amount threshold
    // Note: amountThreshold is stored as raw token units, convert for display
    const displayAmount =
      condition?.amountThreshold && condition?.tokenDecimals != null
        ? toHumanReadableTokenAmount(
            condition.amountThreshold,
            condition.tokenDecimals
          )
        : condition?.amountThreshold;

    if (displayAmount && condition?.tokenSymbol) {
      segments.push({ text: 'Transfer up to', isPill: false });
      segments.push({
        text: `${displayAmount} ${condition.tokenSymbol}`,
        isPill: true,
      });
    } else if (condition?.tokenSymbol) {
      segments.push({ text: 'Transfer', isPill: false });
      segments.push({
        text: `any amount of ${condition.tokenSymbol}`,
        isPill: true,
      });
    } else if (displayAmount) {
      segments.push({ text: 'Transfer up to', isPill: false });
      segments.push({ text: displayAmount, isPill: true });
    } else {
      segments.push({ text: 'Transfer', isPill: false });
      segments.push({ text: 'any amount', isPill: true });
    }

    // Add destination info based on destinationAddresses
    segments.push({ text: 'to', isPill: false });
    const destinationAddresses = condition?.destinationAddresses;
    if (!destinationAddresses || destinationAddresses.length === 0) {
      segments.push({ text: 'any destination', isPill: true });
    } else if (destinationAddresses.length === 1) {
      segments.push({
        text:
          getSingleDestinationLabel(policy) ?? destinationAddresses[0] ?? '',
        isPill: true,
      });
    } else {
      segments.push({
        text: `${destinationAddresses.length} addresses`,
        isPill: true,
      });
    }
  } else if (policy.transactionType === 'CONTRACT_INTERACTION') {
    const condition = policy.contractInteractionCondition;

    if (condition && condition.length > 0) {
      const contractAddr = condition[0];
      const functionNames = contractAddr
        ? [
            ...(contractAddr.proxyFunctions?.map((f) => f.name) ?? []),
            ...(contractAddr.implementationFunctions?.map((f) => f.name) ?? []),
          ]
        : [];

      segments.push({ text: 'Call', isPill: false });

      if (functionNames.length === 1) {
        const isProxy = contractAddr?.proxyFunctions?.length === 1;
        const functionText = isProxy
          ? `${functionNames[0]} (proxy)`
          : functionNames[0] || 'any function';
        segments.push({
          text: functionText,
          isPill: true,
        });
      } else if (functionNames.length > 1) {
        segments.push({
          text: `${functionNames.length} functions`,
          isPill: true,
        });
      } else {
        segments.push({ text: 'any function', isPill: true });
      }

      segments.push({ text: 'on', isPill: false });
      segments.push({
        text: contractAddr?.contractAddress || 'unknown',
        isPill: true,
      });

      if (functionNames.length === 0) {
        segments.push({ text: 'with', isPill: false });
        segments.push({ text: 'any arguments', isPill: true });
      }
    } else {
      segments.push({ text: 'Call', isPill: false });
      segments.push({ text: 'any function', isPill: true });
      segments.push({ text: 'on', isPill: false });
      segments.push({ text: 'any contract', isPill: true });
    }
  } else {
    // ANY transaction type
    segments.push({ text: 'Any transaction type', isPill: false });
  }

  return segments;
}

/**
 * Gets the display text for policy type (top-right corner)
 */
export function getPolicyTypeDisplay(policy: Policy): string {
  if (policy.transactionType === 'TOKEN_TRANSFER') {
    return 'Token Transfer';
  } else if (policy.transactionType === 'CONTRACT_INTERACTION') {
    return 'Contract Interaction';
  } else {
    return 'Any Transaction';
  }
}

/**
 * Gets the display text for policy approval status (bottom-right corner)
 */
export function getPolicyApprovalDisplay(policy: Policy): string {
  if (policy.type === 'AUTO_APPROVAL') {
    return 'Automatic Approval';
  } else if (policy.type === 'MANUAL_APPROVAL') {
    // The API should include threshold info in the policy object
    // For now, return generic text - this will be updated when backend provides the data
    return 'Manual Approval';
  } else {
    return 'Unknown';
  }
}
